/**
 * `Ajustes → Plano e consumo` — F7 da spec 16, a tela do §6 da spec 14 que a ADR-0012 encolheu.
 *
 * ## O que ela mostra, e o que ela deliberadamente NÃO faz
 *
 * Mostra conversas do mês contra a franquia, somando entre os números, e o consumo de cada número.
 * **Não oferece botão para comprar mais, nem avisa que vai cortar** — porque não corta: §4 da
 * ADR-0012, *"o envio nunca é cortado"*. Uma barra vermelha com "limite atingido" ensinaria o
 * cliente a esperar um bloqueio que não existe, e ele pararia de mandar mensagem por medo.
 *
 * ## A frase vem do servidor
 *
 * `explicacao` é montada em `src/painel/plano.ts`. Reescrevê-la aqui criaria duas verdades sobre a
 * mesma conta — e a da tela é a que fica desatualizada quando a regra muda.
 *
 * ## Franquia nula não é zero
 *
 * Tenant sem franquia registrada mostra o consumo e diz que a franquia não está aqui. "0 de 0" leria
 * como estouro imediato.
 */

import { useState } from "react";
import { EstadoDaFalha, falhaDe, identificadorLegivel, useSessao } from "@hugi/ui";
import { Carregando } from "@hugi/ui/kit";
import { Acao, Situacao } from "./Gerais.js";
import {
  useAssinatura,
  useCancelarAssinatura,
  useReativarAssinatura,
  useComprarNumeros,
  useConsumoDoPlano,
  useDefinirExcedenteDeIa,
  type ConsumoDoPlano,
} from "./dados.js";

const NOME_DO_PLANO: Readonly<Record<string, string>> = {
  starter: "Starter",
  professional: "Profissional",
  api: "Hugi API",
  completo: "Hugi Completo",
};

/**
 * Data legível a partir do ISO do servidor.
 *
 * ⚠️ `new Date(iso)` direto é o defeito que o checkout já pagou: `new Date("2026-09-19")` é meia-noite
 * **UTC** e vira dia 18 no Brasil. As datas daqui vêm com hora (timestamptz), então o construtor está
 * certo — mas a nota fica, porque a próxima que chegar sem hora quebra em silêncio.
 */
function data(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("pt-BR");
}

/**
 * SUA ASSINATURA — plano, ciclo, valor, prazo, status e o cancelamento.
 *
 * ⚠️ Ela mora **aqui**, e não no painel do Cert4All, porque o cliente não entra lá. O que ele
 * contratou foi o Hugi, e é aqui que ele procura *"qual o meu plano, até quando, como cancelo"*.
 * A autoridade continua sendo o Cert4All: esta tela não calcula nada — a frase, a conta e o prazo
 * vêm de lá. Ver `src/painel/assinaturaDoCert4All.ts`.
 *
 * ⚠️ E a conta aparece **antes** do clique. Cancelar um anual devolve dinheiro, e quem clica precisa
 * ver quanto ANTES — não descobrir depois.
 */
function SuaAssinatura() {
  const [confirmando, setConfirmando] = useState(false);
  const assinatura = useAssinatura();
  const cancelar = useCancelarAssinatura();
  const reativar = useReativarAssinatura();

  if (assinatura.isLoading) return null;
  const a = assinatura.data;
  if (!a) return null;

  /*
    ⚠️ "Não consigo mostrar" NÃO é erro de tela. Inclui o 403 de quem não administra a empresa no
    Cert4All — quem administra vê o cartão, quem não administra segue usando o resto da página. Um
    `EstadoDaFalha` aqui faria a página inteira parecer quebrada para o atendente.
  */
  /* Redundante com a guarda de `viva` mais abaixo, e fica: ela DECLARA que os dois casos somem da
     tela, em vez de deixar isso como efeito colateral de um `!==` lá embaixo. */
  if (a.tipo === "indisponivel" || a.tipo === "sem_assinatura") return null;

  /*
    ⚠️ QUEM DIZ SE ESTÁ CANCELADA É O SERVIDOR — e isso é o conserto de um defeito achado clicando.
 
    A primeira versão era `cancelar.data ?? (servidor)`. O resultado de uma mutação do React Query
    **fica no estado para sempre**, então depois de REATIVAR o cartão continuava preso em
    "Cancelada", sem botão, até alguém recarregar a página — com o servidor já respondendo "viva".
    A pessoa desfazia o cancelamento e a tela insistia que ela tinha ido embora.
 
    Da mutação vem só o que o GET não devolve: os AVISOS daquela chamada (a cobrança que não parou,
    por exemplo). E as duas mutações se resetam uma à outra no clique, senão a segunda volta a
    envenenar a primeira no ciclo seguinte.
  */
  const fechada =
    a.tipo === "cancelada"
      ? { ...a.cancelamento, avisos: cancelar.data?.avisos ?? [] }
      : null;

  if (fechada) {
    return (
      <div className="hugi-ajustes-bloco">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-medium">Sua assinatura</h3>
          <Situacao estado="degradado" rotulo="Cancelada" />
        </div>
        <p className="text-sm">{fechada.motivo}</p>
        <p className="text-sm text-[color:var(--tinta-fraca)]">
          O serviço continua até {data(fechada.acessoAte)}.
          {fechada.reembolsoCentavos > 0
            ? ` A devolução de ${reais(fechada.reembolsoCentavos)} entra na conta usada no pagamento.`
            : ""}
        </p>
        {(fechada.avisos ?? []).map((av) => (
          <p key={av} className="text-sm text-[color:var(--atencao-texto)]">
            {av}
          </p>
        ))}

        {/*
          ⚠️ "Voltar atrás" só aparece enquanto DÁ — e quem sabe é o servidor (`podeReativar`).
          Depois do encerramento a credencial do canal já foi apagada do cofre, e reconectar exige
          QR novo; oferecer o botão ali prometeria o que nenhum clique desfaz.

          E ele é SECUNDÁRIO, ao lado do que já aconteceu: quem chegou aqui decidiu sair, e um botão
          primário de "ficar" seria o produto discutindo a decisão de quem já a tomou.
        */}
        {/* Só `podeReativar`: depois de reativar o servidor responde "viva", `fechada` fica null e este
            bloco inteiro deixa de existir — não é preciso olhar o estado da mutação. */}
        {a.tipo === "cancelada" && a.podeReativar ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-[color:var(--tinta-fraca)]">
              Mudou de ideia? Dá para voltar atrás enquanto o serviço não termina.
            </p>
            <div>
              <Acao
                tom="secundaria"
                onClick={() => {
                  cancelar.reset();
                  /*
                    ⚠️ E FECHA A CONFIRMAÇÃO. Achado clicando: o `confirmando` sobrevive ao ciclo
                    inteiro (ele fica escondido enquanto o cartão está fechado), e quem reativava
                    voltava para a tela com "Confirmar cancelamento" JÁ ABERTO — um clique acidental
                    de distância de cancelar de novo, logo depois de ter decidido ficar.
                  */
                  setConfirmando(false);
                  reativar.mutate();
                }}
                desabilitado={reativar.isPending}
                motivo="Reativando — aguarde a resposta do Cert4All."
              >
                {reativar.isPending ? "Reativando…" : "Voltar atrás e manter a assinatura"}
              </Acao>
            </div>
            {reativar.isError ? (
              <p className="text-sm text-[color:var(--erro-texto)]">
                {falhaDe(reativar.error)?.erro?.mensagem ?? "Não foi possível reativar agora."}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (a.tipo !== "viva") return null;
  const { assinatura: s, seCancelarAgora: p } = a;

  return (
    <div className="hugi-ajustes-bloco">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium">Sua assinatura</h3>
        <Situacao estado="ok" rotulo="Ativa" />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <span>
          <span className="text-[color:var(--tinta-fraca)]">Plano: </span>
          <strong>{NOME_DO_PLANO[s.plano] ?? s.plano}</strong>
        </span>
        <span>
          <span className="text-[color:var(--tinta-fraca)]">Cobrança: </span>
          <strong>
            {reais(s.pagoCentavos)} {s.ciclo === "anual" ? "por ano" : "por mês"}
          </strong>
        </span>
        <span>
          {/* "Prevista" porque é derivada do contrato, e não lida do provedor a cada abertura. */}
          <span className="text-[color:var(--tinta-fraca)]">Próxima renovação (prevista): </span>
          <strong>{data(s.renovaEm)}</strong>
        </span>
        <span>
          <span className="text-[color:var(--tinta-fraca)]">Desde: </span>
          <strong>{data(s.inicioEm)}</strong>
        </span>
      </div>

      {confirmando ? (
        <div className="flex flex-col gap-3">
          {/*
            ⚠️ A confirmação REPETE o número. Um "tem certeza?" genérico faria a pessoa decidir sem o
            dado que importa — e aqui o dado é quanto ela recebe de volta.
          */}
          <p className="text-sm">{p.motivo}</p>
          <p className="text-sm text-[color:var(--tinta-fraca)]">
            O serviço fica no ar até {data(p.acessoAte)}, e não há nova cobrança.
          </p>
          <div className="flex flex-wrap gap-2">
            <Acao
              tom="perigo"
              onClick={() => {
                  reativar.reset();
                  cancelar.mutate();
                }}
              desabilitado={cancelar.isPending}
              motivo="Cancelando — aguarde a resposta do Cert4All."
            >
              {cancelar.isPending ? "Cancelando…" : "Confirmar cancelamento"}
            </Acao>
            <Acao
              tom="secundaria"
              onClick={() => setConfirmando(false)}
              desabilitado={cancelar.isPending}
              motivo="Cancelando — aguarde a resposta do Cert4All."
            >
              Voltar
            </Acao>
          </div>
          {cancelar.isError ? (
            <p className="text-sm text-[color:var(--erro-texto)]">
              {falhaDe(cancelar.error)?.erro?.mensagem ?? "Não foi possível cancelar agora."}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-[color:var(--tinta-fraca)]">
            Se cancelar hoje
          </p>
          {/*
            ⚠️ A frase vem do SERVIDOR. Montada aqui, ela seria mais uma cópia da política — e erraria
            justamente no caso que mais importa: dentro dos 7 dias do art. 49 do CDC a devolução é
            INTEGRAL, e um texto local diria "0 meses iniciados são recalculados pelo preço mensal".
          */}
          <p className="text-sm text-[color:var(--tinta-fraca)]">{p.motivo}</p>
          <div>
            <Acao tom="secundaria" onClick={() => setConfirmando(true)}>
              Cancelar assinatura
            </Acao>
          </div>
        </div>
      )}
    </div>
  );
}

function reais(centavos: number): string {
  return (centavos / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/**
 * O estado do selo por situação.
 *
 * `estourou` é **`degradado`, não `ruim`** — e a diferença é a decisão da ADR-0012: passar da
 * franquia não quebra nada, vira linha na fatura. O vermelho de `ruim` ensinaria o cliente a esperar
 * um bloqueio que não existe, e ele pararia de mandar mensagem por medo.
 */
/**
 * A FRANQUIA DE IA (ADR-0018): o segundo medidor, e o opt-in do excedente com teto de gasto.
 *
 * Não aparece quando o plano não define franquia de IA (`franquiaIa` nulo) — é o comportamento
 * anterior à ADR, e um cartão dizendo "sem limite" seria ruído. Com franquia zero (Hugi API) diz
 * que o plano não inclui o assistente e para aí.
 */
function FranquiaDeIa({ d }: { readonly d: ConsumoDoPlano }) {
  const definir = useDefinirExcedenteDeIa();
  const [autorizado, setAutorizado] = useState(d.excedenteDeIaAutorizado);
  const [teto, setTeto] = useState(d.tetoDeGastoIaCentavos === null ? "" : String(d.tetoDeGastoIaCentavos / 100));
  if (d.franquiaIa === null) return null;
  if (d.franquiaIa === 0) {
    return (
      <div className="hugi-ajustes-bloco">
        <h3 className="text-sm font-medium">Conversas com o assistente</h3>
        <p className="text-sm text-[color:var(--tinta-fraca)]">{d.explicacaoIa}</p>
      </div>
    );
  }
  const porcento = d.fracaoIa === null ? null : Math.round(d.fracaoIa * 100);
  const tetoCentavos = teto.trim() === "" ? null : Math.round(Number(teto.replace(",", ".")) * 100);
  const tetoInvalido = teto.trim() !== "" && (!Number.isFinite(tetoCentavos) || (tetoCentavos ?? -1) < 0);
  const mudou = autorizado !== d.excedenteDeIaAutorizado || (tetoCentavos ?? null) !== (d.tetoDeGastoIaCentavos ?? null);

  return (
    <div className="hugi-ajustes-bloco">
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="text-sm font-medium">Conversas com o assistente</h3>
        <span className="text-2xl font-semibold text-[color:var(--tinta-forte)]">
          {d.conversasIa.toLocaleString("pt-BR")}
        </span>
        <span className="text-sm text-[color:var(--tinta-fraca)]">
          de {d.franquiaIa.toLocaleString("pt-BR")} incluídas
        </span>
        <span className="ml-auto">
          <Situacao
            estado={d.agenteParado ? "degradado" : estadoDaSituacao(d.situacaoIa)}
            rotulo={
              d.agenteParado
                ? "Assistente parado"
                : d.situacaoIa === "estourou"
                  ? "Com excedente"
                  : d.situacaoIa === "perto"
                    ? "Mais de 80%"
                    : "Dentro do plano"
            }
          />
        </span>
      </div>

      {porcento === null ? null : (
        <div
          className="h-2 w-full overflow-hidden rounded-full"
          style={{ background: "var(--borda)" }}
          role="img"
          aria-label={`${porcento}% da franquia de conversas com o assistente`}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, porcento)}%`,
              background: d.situacaoIa === "ok" ? "var(--acao-forte)" : "var(--atencao)",
            }}
          />
        </div>
      )}

      <p className="text-sm text-[color:var(--tinta-fraca)]">{d.explicacaoIa}</p>

      {d.excedenteIa > 0 && d.excedenteIaEstimadoCentavos !== null ? (
        <p className="text-sm">
          Excedente de IA estimado: <strong>{reais(d.excedenteIaEstimadoCentavos)}</strong>{" "}
          <span className="text-[color:var(--tinta-fraca)]">
            ({d.excedenteIa.toLocaleString("pt-BR")} conversa(s), já descontado o que pagaram como excedente comum)
          </span>
        </p>
      ) : null}

      {/*
        O OPT-IN. Sem ele, o assistente para ao esgotar e a equipe continua — é o padrão, e a
        frase acima já disse. Com ele, o cliente escolhe até quanto aceita gastar no mês.
      */}
      <div className="flex flex-col gap-2 border-t border-[color:var(--borda)] pt-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={autorizado}
            onChange={(e) => setAutorizado(e.target.checked)}
          />
          <span>
            <strong>Continuar atendendo com o assistente além da franquia</strong>
            {d.excedenteIaPorConversaCentavos !== null ? (
              <span className="text-[color:var(--tinta-fraca)]">
                {" "}— cada conversa a mais custa {reais(d.excedenteIaPorConversaCentavos)}, já com o componente comum dentro.
              </span>
            ) : null}
          </span>
        </label>
        {autorizado ? (
          <label className="flex flex-wrap items-center gap-2 text-sm">
            <span>Teto de gasto no mês (R$)</span>
            <input
              className="hugi-ajustes-numero"
              inputMode="decimal"
              placeholder="sem teto"
              value={teto}
              onChange={(e) => setTeto(e.target.value)}
              aria-invalid={tetoInvalido}
            />
            <span className="text-[color:var(--tinta-fraca)]">Ao chegar nele, o assistente para sozinho.</span>
          </label>
        ) : null}
        <div className="flex items-center gap-2">
          <Acao
            tom="primaria"
            desabilitado={!mudou || tetoInvalido || definir.isPending}
            motivo={definir.isPending ? "Salvando." : tetoInvalido ? "O teto não é um valor válido." : "Nada mudou."}
            onClick={() => definir.mutate({ autorizado, tetoDeGastoCentavos: autorizado ? tetoCentavos : null })}
          >
            {definir.isPending ? "Salvando…" : "Salvar"}
          </Acao>
          {definir.isError ? (
            <span className="text-sm text-[color:var(--erro-texto,var(--erro))]">{falhaDe(definir.error)?.erro?.mensagem ?? "Não foi possível salvar agora."}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function estadoDaSituacao(s: ConsumoDoPlano["situacao"]): "neutro" | "ok" | "degradado" {
  if (s === "estourou" || s === "perto") return "degradado";
  if (s === "ok") return "ok";
  return "neutro";
}

export function Plano() {
  /* Lido aqui também para não oferecer compra a quem cancelou — ver o comentário lá embaixo. */
  const assinatura = useAssinatura();
  const consumo = useConsumoDoPlano();

  if (consumo.isPending) return <Carregando>Carregando…</Carregando>;
  if (consumo.error) {
    return (
      <EstadoDaFalha
        falha={falhaDe(consumo.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
        aoRepetir={() => void consumo.refetch()}
      />
    );
  }

  const d = consumo.data!;
  const porcento = d.fracao === null ? null : Math.round(d.fracao * 100);
  /* Mesmo `queryKey` do cartão acima: o React Query serve do cache, não é uma segunda chamada. */
  const assinaturaCancelada = assinatura.data?.tipo === "cancelada";

  return (
    <div className="hugi-plano">
      <div className="hugi-plano-topo">
        <h2 className="text-base font-medium">Plano e consumo</h2>
        {d.plano ? <Situacao estado="neutro" rotulo={NOME_DO_PLANO[d.plano] ?? d.plano} /> : null}
        <span className="text-sm text-[color:var(--tinta-fraca)]">{d.competencia}</span>
      </div>

      {/*
        ⚠️ A assinatura vem ANTES do medidor, e a ordem é deliberada: quem abre esta tela por causa
        de cobrança procura "qual o meu plano e até quando", não a fração do mês.
      */}
      <SuaAssinatura />

      <div className="hugi-ajustes-bloco">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-2xl font-semibold text-[color:var(--tinta-forte)]">
            {d.conversas.toLocaleString("pt-BR")}
          </span>
          <span className="text-sm text-[color:var(--tinta-fraca)]">
            {d.franquiaTotal === null
              ? "conversas neste mês"
              : `de ${d.franquiaTotal.toLocaleString("pt-BR")} incluídas`}
          </span>
          <span className="ml-auto">
            <Situacao
              estado={estadoDaSituacao(d.situacao)}
              rotulo={
                d.situacao === "indefinido"
                  ? "Franquia não registrada"
                  : d.situacao === "estourou"
                    ? "Com excedente"
                    : d.situacao === "perto"
                      ? "Mais de 80%"
                      : "Dentro do plano"
              }
            />
          </span>
        </div>

        {porcento === null ? null : (
          <div
            className="h-2 w-full overflow-hidden rounded-full"
            /*
              ⚠️ `--borda`, e NÃO `--vidro-controle`.

              A primeira versão usava `--vidro-controle`, que é branco a 82% — sobre o cartão, que é
              branco a 62%, o trilho ficava **invisível**. Medido no navegador: 686×8px de nada.
              É o mesmo defeito do esqueleto do QR, e o efeito aqui é pior: um medidor cujo trilho
              some faz "usei quase nada" parecer "a barra não carregou".

              `--borda` é o tom de separador — escuro a 10% no claro, e redefinido no escuro —,
              então ele aparece sobre o cartão nos dois temas sem competir com o preenchimento.
            */
            style={{ background: "var(--borda)" }}
            role="img"
            aria-label={`${porcento}% da franquia do mês`}
          >
            <div
              className="h-full rounded-full"
              style={{
                // Nunca passa de 100% de LARGURA, mesmo estourando: uma barra que transborda o
                // contêiner lê como defeito de layout, e o número ao lado já diz o quanto passou.
                width: `${Math.min(100, porcento)}%`,
                // FUNDO da barra, nao texto: aqui vale o token semantico, nao o de leitura.
                background: d.situacao === "ok" ? "var(--acao-forte)" : "var(--atencao)",
              }}
            />
          </div>
        )}

        <p className="text-sm text-[color:var(--tinta-fraca)]">{d.explicacao}</p>

        {d.excedente > 0 && d.excedenteEstimadoCentavos !== null ? (
          <p className="text-sm">
            Excedente estimado: <strong>{reais(d.excedenteEstimadoCentavos)}</strong>{" "}
            <span className="text-[color:var(--tinta-fraca)]">
              ({d.excedente.toLocaleString("pt-BR")} × {reais(d.excedentePorConversaCentavos ?? 0)})
            </span>
          </p>
        ) : null}
      </div>

      <FranquiaDeIa d={d} />

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Por número</h3>

        {/*
          ⚠️ CONTRATADOS × CONECTADOS, e os dois números aparecem juntos de propósito.

          A franquia é medida contra o CONTRATADO, não contra o que está ligado. Mostrar só um dos
          dois faria a conta ao lado parecer errada para quem comprou dois e ligou um — ele leria
          "1 número" e "16.000 incluídas", e a divisão não fecharia.

          E é aqui que ele descobre que ainda tem número para usar sem pagar mais nada.
        */}
        {d.numerosContratados === null ? null : (
          <p className="text-sm text-[color:var(--tinta-fraca)]">
            Você contratou{" "}
            <strong className="text-[color:var(--tinta)]">
              {d.numerosContratados} {d.numerosContratados === 1 ? "número" : "números"}
            </strong>{" "}
            e {d.numeros === 1 ? "conectou 1" : `conectou ${d.numeros}`}.{" "}
            {d.numeros < d.numerosContratados
              ? `A franquia já conta ${d.numerosContratados === 1 ? "ele" : "todos"} — conectar o resto não muda a conta.`
              : "Para conectar mais um, compre abaixo."}
          </p>
        )}

        {/*
          ⚠️ NÃO se oferece AUMENTAR A FATURA a quem acabou de cancelar.

          Achado abrindo a tela: depois do cancelamento o cartão dizia "Cancelada" e, três linhas
          abaixo, "Comprar mais um número". Fora o absurdo de leitura, a compra criaria uma cobrança
          proporcional numa assinatura que está sendo encerrada — e a conta do reembolso já foi
          calculada e registrada.
        */}
        {d.numerosContratados === null || assinaturaCancelada ? null : (
          <ComprarNumeros contratados={d.numerosContratados} />
        )}

        {/*
          PUBLICAÇÕES EM GRUPOS: dito só para quem TEM o direito, e com o teto por empresa. O
          controle negativo importa: quem não tem não lê "0 grupos", que soaria como estouro — não
          lê nada, porque o módulo não faz parte do plano dele.
        */}
        {!d.publicacoesEmGrupos ? null : (
          <p className="text-sm text-[color:var(--tinta-fraca)]">
            Seu plano inclui <strong className="text-[color:var(--tinta)]">Publicações em Grupos</strong>
            {d.gruposIncluidos === null
              ? "."
              : ` — até ${d.gruposIncluidos.toLocaleString("pt-BR")} grupos gerenciados, somando todos os números.`}
          </p>
        )}

        {d.porNumero.length === 0 ? (
          <p className="text-sm text-[color:var(--tinta-fraca)]">
            Nenhum número conectado ainda. A franquia é por número, então ela começa a contar quando
            o primeiro entrar.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {d.porNumero.map((n) => (
              <li key={n.conexaoId} className="hugi-ajustes-item">
                <span className="min-w-0 flex-1 truncate text-sm">
                  {n.numeroExibicao ? identificadorLegivel(n.numeroExibicao) : "(sem número de exibição)"}
                  <span className="ml-2 text-xs text-[color:var(--tinta-fraca)]">{n.canal}</span>
                </span>
                <span className="text-sm tabular-nums">{n.conversas.toLocaleString("pt-BR")}</span>
              </li>
            ))}
          </ul>
        )}
        {/*
          A explicação do teto que soma. Ela existe porque o comportamento é generoso e
          contraintuitivo: quem vê "7.000" em um número e "1.000" no outro tende a achar que o
          primeiro estourou sozinho.
        */}
        {d.franquiaTotal === null ? null : (
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            O limite <strong>soma</strong> entre os seus números: a folga de um cobre o outro.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * COMPRAR MAIS NÚMEROS — o quarto e último degrau do self-service do plano API.
 *
 * ## O que a tela NÃO faz, e é decisão
 *
 * Ela **não calcula o preço**. O proporcional depende do vencimento do ciclo, que só o Asaas conhece,
 * e a frase que aparece depois vem do servidor — a MESMA conta que gerou a cobrança. Estimar aqui
 * criaria um segundo lugar onde o valor nasce, e é assim que o cartaz passa a dizer um número e a
 * fatura outro.
 *
 * ## A chave de idempotência nasce com o COMPONENTE
 *
 * ⚠️ Ela é gerada uma vez e sobrevive à retentativa: é o que faz "apertei duas vezes" ser **uma**
 * cobrança. Só troca quando a quantidade muda — aí é outra compra. Gerá-la a cada envio devolveria
 * exatamente o defeito que ela existe para impedir.
 */
function ComprarNumeros({ contratados }: { contratados: number }) {
  const sessao = useSessao();
  const comprar = useComprarNumeros();
  const [quantos, setQuantos] = useState(contratados + 1);
  const [chave] = useState(() => `num-${crypto.randomUUID()}`);

  /*
    ⚠️ `purchase_plan`, e o botão não existe para quem não tem.

    Não é `esconder o que o servidor recusaria`: é a mesma permissão que a rota exige. Mostrar um
    botão que devolve 403 é o defeito espelhado do botão morto — ele promete o que o servidor nega.
  */
  if (!sessao.permissoes.includes("purchase_plan")) return null;

  const feito = comprar.data;
  const falha = comprar.error ? falhaDe(comprar.error) : null;

  return (
    <div className="hugi-ajustes-bloco">
      {/*
        O TÍTULO MUDA COM O DESFECHO, e isto foi visto na tela: depois de comprar, o cartão continuava
        perguntando "precisa de mais números?" acima da confirmação — lendo como se nada tivesse
        acontecido, ao lado de uma frase dizendo que aconteceu.
      */}
      <h3 className="text-sm font-medium">{feito ? "Pronto — números adicionados" : "Precisa de mais números?"}</h3>

      {feito ? (
        <>
          {/* A frase do SERVIDOR, inteira. Ver o cabeçalho. */}
          <p className="text-sm">{feito.explicacao}</p>
          {feito.jaEstavaFeito ? (
            <p className="text-xs text-[color:var(--tinta-fraca)]">
              Esta compra já tinha sido concluída — você não foi cobrado de novo.
            </p>
          ) : null}
        </>
      ) : (
        <>
          <p className="text-sm text-[color:var(--tinta-fraca)]">
            Você entra com eles na hora. O que falta do ciclo atual é cobrado à parte,{" "}
            <strong className="text-[color:var(--tinta)]">proporcional aos dias que sobram</strong> — e
            a mensalidade só muda a partir da próxima cobrança.
          </p>

          {/*
            ⚠️ `vidro-controle` no fundo, e NÃO a classe `campo` do site — ela não existe aqui.

            A primeira versão usava `campo`/`campo-rotulo`, que são do `web/site`. No painel elas não
            casam com regra nenhuma, e o resultado, medido no navegador, era um campo **sem borda,
            sem fundo e sem padding**: um "3" solto no cartão, indistinguível de texto. O typecheck
            não vê classe de CSS, e o teste em JSDOM não desenha — só abrir a tela mostra.

            É a família do `botao-fantasma` e do `painel-olho` reusado: **classe carrega o contexto
            que ela supõe**, e reaproveitá-la de outro build é herdar um contexto que não existe.
          */}
          <label className="flex max-w-[12rem] flex-col gap-1">
            <span className="text-xs text-[color:var(--tinta-fraca)]">Quantos números no total</span>
            <input
              type="number"
              min={contratados + 1}
              max={20}
              step={1}
              value={quantos}
              disabled={comprar.isPending}
              className="rounded px-2 py-1 text-sm"
              style={{ background: "var(--vidro-controle)" }}
              onChange={(e) => {
                const n = Math.trunc(Number(e.target.value));
                /*
                  A tela CORRIGE em vez de recusar no envio: o mínimo é sempre "um a mais do que
                  você tem", porque por aqui só dá para aumentar — reduzir envolve escolher qual
                  número desligar, e isso não é decisão de um botão.
                */
                setQuantos(Number.isFinite(n) ? Math.min(Math.max(n, contratados + 1), 20) : contratados + 1);
              }}
            />
          </label>

          <div>
            <Acao
              tom="primaria"
              onClick={() => comprar.mutate({ numerosContratados: quantos, chaveDeIdempotencia: chave })}
              desabilitado={comprar.isPending}
            >
              {comprar.isPending ? "Comprando…" : `Comprar ${quantos - contratados > 1 ? `${quantos - contratados} números` : "mais um número"}`}
            </Acao>
          </div>

          {falha ? (
            /*
              A FRASE VEM DO SERVIDOR — as recusas dele (`NAO_AUMENTA`, `NUMEROS_ACIMA_DO_TETO`,
              `ASSINATURA_INATIVA`) já explicam o que fazer, e o 503 já diz que repetir não cobra
              duas vezes. Reescrevê-las aqui apagaria a instrução.
            */
            <p className="text-sm text-[color:var(--erro-texto)]">
              {falha.erro.mensagem ?? "Não deu para concluir a compra agora."}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
